/**
 * AIResponseBubble Component
 *
 * Renders AI response in progress (typing effect).
 * Waveform removed from bubble - stays in header for cleaner UI.
 *
 * Subtle "fade-in then solidify" effect: when new text arrives (a burst from
 * the synced reveal), the newly-revealed tail starts at 50% opacity and
 * transitions to 100% over ~500ms. Once the next burst arrives, the previous
 * tail becomes solid and the newest tail gets the fade. This makes each word
 * "pop" in softly rather than appearing at full opacity instantly — a subtle
 * polish inspired by the ChatGPT app.
 */

import { useEffect, useRef, useState } from 'react';
import { getLocalizedCharacterName, MultilingualCharacterName } from '../utils/characterName';

interface AIResponseBubbleProps {
  text: string;
  avatarImageUrl?: string;
  characterName?: string | MultilingualCharacterName;
}

/** How long (ms) the newly-revealed tail takes to solidify from 50% → 100%. */
const SOLIDIFY_MS = 500;
/** Starting opacity for a freshly-revealed burst. */
const FADE_START_OPACITY = 0.5;

/**
 * Renders the most recently-revealed text tail, fading it in from
 * FADE_START_OPACITY → 1 over SOLIDIFY_MS. Remounts (via `key`) on each new
 * burst so the animation re-triggers cleanly.
 */
function FadingTail({ text }: { text: string }) {
  const [solidified, setSolidified] = useState(false);

  useEffect(() => {
    // Mount at FADE_START_OPACITY, then on the next frame begin the transition
    // to full opacity. Two rAFs ensure the browser paints the start state first
    // (so the transition actually animates rather than jumping to 1 instantly).
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSolidified(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  return (
    <span
      style={{
        opacity: solidified ? 1 : FADE_START_OPACITY,
        transition: `opacity ${SOLIDIFY_MS}ms ease-out`,
      }}
    >
      {text}
    </span>
  );
}

export default function AIResponseBubble({
  text,
  avatarImageUrl,
  characterName
}: AIResponseBubbleProps) {
  const localizedName = getLocalizedCharacterName(characterName);

  // Track the split between "solid" (already-solidified) text and the
  // "fading" tail (most recent burst). When text grows, the previous fading
  // tail is promoted to solid, and the new growth becomes the fading tail.
  const solidLengthRef = useRef(0);
  const [solidLength, setSolidLength] = useState(0);

  useEffect(() => {
    if (!text) {
      // Response cleared (new response) — reset everything.
      solidLengthRef.current = 0;
      setSolidLength(0);
      return;
    }

    // If text grew, promote the previous fading tail to solid and start a new
    // fade on the newly-revealed portion.
    if (text.length > solidLengthRef.current) {
      setSolidLength(solidLengthRef.current);
      solidLengthRef.current = text.length;
    }
    // If text shrank or stayed the same, do nothing — keep current state.
  }, [text]);

  const solidText = text.substring(0, solidLength);
  const fadingText = text.substring(solidLength);

  return (
    <div className="flex items-start gap-2 md:gap-3 justify-start pl-3 md:pl-8">
      {avatarImageUrl && (
        <img
          src={avatarImageUrl}
          alt={localizedName}
          className="w-6 h-6 md:w-8 md:h-8 rounded-full object-cover flex-shrink-0 opacity-70"
        />
      )}
      <div className="p-2 md:p-3 rounded-lg bg-gray-800/30 backdrop-blur-sm border border-gray-600/10 max-w-[75%] md:max-w-md">
        <span className="text-gray-300 text-xs md:text-sm italic leading-relaxed">
          {solidText}
          {fadingText && (
            <FadingTail key={solidLength} text={fadingText} />
          )}
        </span>
      </div>
    </div>
  );
}
